/**
 * Supplier-matching helper — SCM13.
 *
 * Given a set of compliance requirements (BoM-item display names, taxonomy
 * ids, or a mix), return the suppliers from the active plan that satisfy
 * ALL of them. Used by:
 *
 *   - SupplyChainBomPanel    → per-row "N suppliers match" affordance
 *   - SupplyChainSuppliersPanel → "Match BoM requirements" preset filter
 *
 * Matching semantics:
 *
 *   - The supplier must hold EVERY requirement (AND-semantics) — partial
 *     overlaps don't count. This mirrors how an EPC procurement team
 *     reads the BoM requirement list: any missing cert is a disqualifier.
 *   - Display names + taxonomy ids are reconciled through
 *     `normalizeComplianceId`. So a BoM item that says `'NQA-1'` matches a
 *     supplier whose certification list contains `'nqa-1'`.
 *   - An empty requirement list trivially returns every supplier in the
 *     plan (a "no filter applied" superset).
 *
 * Pure function — no React, no Zustand. Tests pin the contract.
 */
import type {
  BomItem,
  ComplianceCertificationId,
  EquipmentCatalogEntry,
  Supplier,
  SupplyChainPlan,
} from '../types/supplyChain';
import { normaliseCertification } from '../types/supplyChain';
import { normalizeComplianceId } from './supplyChainBaselines';

/** A `BomItem` slice the matcher can read, or just a string[] of raw
 *  requirement display-names / ids. */
export type RequirementsSource =
  | Pick<BomItem, 'complianceRequirements'>
  | readonly string[]
  | undefined
  | null;

/** Resolve any of the accepted shapes to a deduped array of normalised ids. */
export function normalizeRequirements(source: RequirementsSource): ComplianceCertificationId[] {
  if (!source) return [];
  const raw: readonly string[] = Array.isArray(source)
    ? (source as readonly string[])
    : (source as Pick<BomItem, 'complianceRequirements'>).complianceRequirements ?? [];
  const seen = new Set<string>();
  const out: ComplianceCertificationId[] = [];
  for (const r of raw) {
    if (typeof r !== 'string' || r.length === 0) continue;
    const id = normalizeComplianceId(r);
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

/**
 * Returns suppliers from `plan.suppliers` that hold ALL of `requirements`.
 *
 * @param plan - SupplyChainPlan (or undefined; empty plan returns []).
 * @param requirements - BoM item or raw string list of cert names/ids.
 */
export function suppliersMatchingRequirements(
  plan: SupplyChainPlan | undefined,
  requirements: RequirementsSource,
): Supplier[] {
  if (!plan) return [];
  const needed = normalizeRequirements(requirements);
  // Empty filter → superset (every supplier in the plan).
  if (needed.length === 0) return plan.suppliers.slice();
  return plan.suppliers.filter((s) => supplierHasAll(s, needed));
}

/** Recorded links establish an equipment candidate, never availability or an award. */
export function supplierEquipmentMatchEvidence(
  plan: SupplyChainPlan | undefined,
  item: BomItem,
  supplier: Supplier,
  catalog: readonly EquipmentCatalogEntry[] = [],
): readonly string[] {
  const evidence: string[] = [];
  if (item.awardedSupplierId === supplier.id || item.shortlistedSupplierIds?.includes(supplier.id)) {
    evidence.push('Linked to this BOM item');
  }
  if (plan?.quotes.some(quote => quote.bomItemId === item.id && quote.supplierId === supplier.id)) {
    evidence.push('Quote record for this item');
  }
  if (item.oemGenerationBasis?.supplierIds.includes(supplier.id)
    || item.oemElectricalMachineBasis?.supplierIds.includes(supplier.id)) {
    evidence.push('Recorded OEM supplier link');
  }
  const identifiesEquipment = (entry: EquipmentCatalogEntry): boolean => (
    (item.equipmentCatalogEntryId != null && entry.id === item.equipmentCatalogEntryId)
    || (item.oemGenerationBasis != null
      && entry.oemGenerationProductId === item.oemGenerationBasis.productId
      && entry.oemPerformanceCaseId === item.oemGenerationBasis.performanceCaseId)
    || (item.oemElectricalMachineBasis != null
      && entry.oemElectricalMachineId === item.oemElectricalMachineBasis.machineId
      && entry.oemElectricalRatingCaseId === item.oemElectricalMachineBasis.ratingCaseId)
  );
  if (catalog.some(entry => identifiesEquipment(entry) && entry.supplierIds?.includes(supplier.id))
    || supplier.catalog?.some(identifiesEquipment)) {
    evidence.push('Exact equipment catalog link');
  }
  return evidence;
}

/**
 * Match a selected equipment line using its recorded product/OEM/quote links
 * AND every certification requirement. Category or name similarity alone is
 * directory discovery, not evidence that a supplier offers this exact model.
 * The supplied directory remains scoped by the normal tenant resolver.
 */
export function suppliersMatchingBomItem(
  plan: SupplyChainPlan | undefined,
  item: BomItem | undefined,
  suppliers: readonly Supplier[] = plan?.suppliers ?? [],
  catalog: readonly EquipmentCatalogEntry[] = [],
): Supplier[] {
  if (!item) return [];
  const needed = normalizeRequirements(item);
  return suppliers.filter(supplier => (
    supplierHasAll(supplier, needed)
    && supplierEquipmentMatchEvidence(plan, item, supplier, catalog).length > 0
  ));
}

/** True iff the supplier's normalised cert set is a superset of `needed`. */
export function supplierHasAll(supplier: Supplier, needed: readonly ComplianceCertificationId[]): boolean {
  if (needed.length === 0) return true;
  const have = new Set((supplier.certifications ?? []).map((c) => normalizeComplianceId(normaliseCertification(c).id)));
  for (const id of needed) {
    if (!have.has(id)) return false;
  }
  return true;
}

/**
 * Aggregate every certification referenced by `plan.bom[*].complianceRequirements`.
 * Used by the Suppliers tab's "Match BoM requirements" preset to populate
 * the chip selection in one click. Deduped + normalised to taxonomy ids.
 */
export function bomRequirementSet(plan: SupplyChainPlan | undefined): ComplianceCertificationId[] {
  if (!plan) return [];
  const seen = new Set<string>();
  const out: ComplianceCertificationId[] = [];
  for (const item of plan.bom) {
    for (const r of item.complianceRequirements ?? []) {
      const id = normalizeComplianceId(r);
      if (!seen.has(id)) {
        seen.add(id);
        out.push(id);
      }
    }
  }
  return out;
}

/**
 * Frequency map: how many suppliers in the plan carry each certification.
 * Used by the Suppliers tab to surface the most-applicable chips first and
 * to hide chips with zero matches in the current shortlist.
 */
export function certificationCounts(plan: SupplyChainPlan | undefined): Map<ComplianceCertificationId, number> {
  const counts = new Map<ComplianceCertificationId, number>();
  if (!plan) return counts;
  for (const s of plan.suppliers) {
    for (const c of s.certifications ?? []) {
      const id = normalizeComplianceId(normaliseCertification(c).id);
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
  }
  return counts;
}
